import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { ClimbCoverImage } from './ClimbCoverImage';
import type { Climb } from '../../types/climb';
import type { ReactNode } from 'react';

interface ClimbImageLightboxProps {
  climbs: Climb[];
  initialIndex: number;
  onClose: () => void;
  onShare?: (climb: Climb) => void;
  onNavigate?: (climb: Climb) => void;
  onRate?: (climb: Climb) => void;
  userAuthenticated?: boolean;
  adminActions?: (climb: Climb) => ReactNode;
  renderOverlay?: (climb: Climb) => ReactNode;
}

export function ClimbImageLightbox({
  climbs,
  initialIndex,
  onClose,
  onShare,
  onNavigate,
  onRate,
  userAuthenticated = false,
  adminActions,
  renderOverlay,
}: ClimbImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(
    Math.max(0, Math.min(initialIndex, climbs.length - 1))
  );

  const climb = climbs[currentIndex];
  const hasPrev = climbs.length > 1;
  const hasNext = climbs.length > 1;

  const goPrev = () =>
    setCurrentIndex((i) => (i > 0 ? i - 1 : climbs.length - 1));
  const goNext = () =>
    setCurrentIndex((i) => (i < climbs.length - 1 ? i + 1 : 0));

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!climb) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
      data-testid="climb-image-lightbox"
    >
      {/* Close */}
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Prev */}
      {hasPrev && (
        <button
          className="absolute left-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2 rounded-full bg-black/40 hover:bg-black/70 transition-colors"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Previous"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {/* Image card — stop propagation so clicks don't close */}
      <div
        className="relative w-full max-w-2xl px-12"
        onClick={(e) => e.stopPropagation()}
      >
        <ClimbCoverImage
          climb={climb}
          onShare={onShare ? () => onShare(climb) : undefined}
          onNavigate={onNavigate ? () => onNavigate(climb) : undefined}
          onRate={onRate ? () => onRate(climb) : undefined}
          userAuthenticated={userAuthenticated}
          className="w-full h-[70vh] rounded-lg shadow-2xl overflow-hidden"
          adminActions={adminActions ? adminActions(climb) : undefined}
        />
        {renderOverlay?.(climb)}
      </div>

      {/* Next */}
      {hasNext && (
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2 rounded-full bg-black/40 hover:bg-black/70 transition-colors"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Next"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Counter */}
      {climbs.length > 1 && (
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm"
          data-testid="lightbox-counter"
        >
          {currentIndex + 1} / {climbs.length}
        </div>
      )}
    </div>
  );
}
